import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Coupon, CouponDetail } from './coupon';
import { User, useUser } from '../../../context/UserContext';
import UserService from '../../../service/User';
import PrimiumConfirm from './PrimiumConfirm';
import CouponService from '../../../service/coupon/Coupon';
import { formatDateToDot } from '../../../func/func';

interface Props {
  couponId: number;
  onClose: () => void;
  onConfirm: () => void;
}

function UseCouponModal({ couponId, onClose, onConfirm }: Props) {
  const [step, setStep] = useState<'warn' | 'confirm' | 'complete' | null>(
    null,
  );
  const [couponDetail, setCouponDetail] = useState<CouponDetail | null>(null);

  const handleClick = () => {
    if (step === 'warn') {
      setStep('confirm');
    } else if (step === 'confirm') {
      couponApply();
      setStep('complete');
    } else {
      onConfirm();
    }
  };

  const toYMD = (d: Date) => d.toISOString().split('T')[0];

  // 종료일 계산
  const getDisplayEndDate = (d: CouponDetail) => {
    if (d.plan_end) {
      const useDays = Number(d.use_days ?? 0);
      const nextStart = new Date(d.plan_end);

      const end = new Date(nextStart);
      end.setDate(end.getDate() + Math.max(useDays, 0));

      return formatDateToDot(toYMD(end));
    }
    return formatDateToDot(d.end_date);
  };

  const getDetail = async () => {
    try {
      const res = await CouponService.detail(couponId);
      if (
        res.data.plan_grade === 'P' &&
        res.data.membership.includes('스탠다드')
      ) {
        setStep('warn');
      } else {
        setStep('confirm');
      }
      setCouponDetail(res.data);
    } catch (e) {
      console.log(e);
    }
  };

  // 쿠폰 사용
  const couponApply = async () => {
    try {
      await CouponService.apply(couponId);
    } catch (e) {
      alert(e);
      console.log(e);
    }
  };

  useEffect(() => {
    getDetail();
  }, []);

  return (
    <ModalOverlay
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <ModalContainer
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {couponDetail &&
          (step === 'warn' ? (
            <PrimiumConfirm
              couponDetail={couponDetail}
              onConfirm={handleClick}
              onClose={onClose}
            />
          ) : (
            <>
              <Header>
                <Title>쿠폰 사용</Title>
                <Content>
                  <ContentTextBox>
                    <ContentTitle>{couponDetail.membership}</ContentTitle>
                    <ContentTitleBlue>{couponDetail.name}</ContentTitleBlue>
                  </ContentTextBox>

                  {couponDetail.plan_grade && (
                    <ContentDesciptionBox>
                      <DateDescription>기존 이용기간</DateDescription>
                      <ContentTextBox>
                        <StartDate>
                          {formatDateToDot(couponDetail.plan_start)} ~
                        </StartDate>
                        <PlanEndDate>
                          {formatDateToDot(couponDetail.plan_end)}
                        </PlanEndDate>
                      </ContentTextBox>
                    </ContentDesciptionBox>
                  )}

                  <ContentDesciptionBox>
                    <DateDescription>
                      {step === 'complete'
                        ? '요금제 사용 후'
                        : '요금제 사용 시'}
                    </DateDescription>
                    <ContentTextBox>
                      <StartDate>
                        {formatDateToDot(
                          couponDetail.plan_start ?? couponDetail.start_date,
                        )}{' '}
                        ~
                      </StartDate>
                      <EndDate>{getDisplayEndDate(couponDetail)}</EndDate>
                    </ContentTextBox>
                  </ContentDesciptionBox>
                </Content>
                <DescriptionBox>
                  {step === 'confirm'
                    ? '사용하시겠습니까?'
                    : couponDetail.plan_grade
                      ? '요금제 기간이 연장되었습니다.'
                      : '요금제가 시작되었습니다.'}
                  {step === 'confirm' && (
                    <SubDescription>
                      ※ 사용을 시작하면 취소나 일시정지가 불가합니다.
                    </SubDescription>
                  )}
                </DescriptionBox>
              </Header>
              <ButtonBox>
                {step === 'confirm' && (
                  <>
                    <CloseButton onClick={onClose}>취소</CloseButton>
                    <UseButton onClick={handleClick}>사용하기</UseButton>
                  </>
                )}
                {step === 'complete' && (
                  <UseButton onClick={handleClick} style={{ width: '100%' }}>
                    확인
                  </UseButton>
                )}
              </ButtonBox>
            </>
          ))}
      </ModalContainer>
    </ModalOverlay>
  );
}

export default UseCouponModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #00000085;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
`;

const ModalContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  overflow: hidden;
  box-shadow: 0px 4px 15px 0px #8d95a340;
  padding: 20px 16px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 145%;
  letter-spacing: -2%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 16px;
  border: 1px solid #e1e2e4;
  padding: 20px 16px;
  gap: 16px;
`;

const ContentTextBox = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const ContentTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
`;

const ContentTitleBlue = styled(ContentTitle)`
  color: #008aff;
`;

const ContentDesciptionBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateDescription = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #37383c9c;
`;

const StartDate = styled(DateDescription)`
  color: #616161;
`;

const EndDate = styled(DateDescription)`
  font-weight: 700;
  color: #008aff;
`;

const PlanEndDate = styled(DateDescription)`
  font-weight: 700;
  color: #222;
`;

const DescriptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
`;

const SubDescription = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
  color: #616161;
`;

const ButtonBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
`;

const Button = styled.button`
  width: 100%;
  border-radius: 8px;
  padding: 12px 28px;
  border: none;

  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
`;

const CloseButton = styled(Button)`
  background: #70737c14;
  color: #616161;
`;

const UseButton = styled(Button)`
  background: #008aff;
  color: #ffffff;
`;
